import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useSEO } from '../../utils/seo';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}

interface VideoTutorial {
  id: string;
  title: string;
  duration: string;
  thumbnail: string;
  videoUrl: string;
  category: string;
}

export default function HelpCenterPage() {
  // SEO Configuration
  useSEO({
    title: 'Help Center & Support | MtaPlannerPro Australia | Tradie Software Tutorials & FAQs',
    description: 'Get help with MtaPlannerPro. Access 120+ articles, 45+ video tutorials, live chat support, and FAQs. Learn how to create quotes, send invoices, track GPS, integrate with Xero, and more. 24/7 support available for Australian tradies.',
    keywords: 'MtaPlannerPro help, tradie software support, quote software tutorials, invoice help, GPS tracking guide, Xero integration help, construction software FAQ, Australian tradie support',
    canonical: '/help-center',
    structuredData: [
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'How do I get started with MtaPlannerPro?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Getting started is easy! Sign up for a 7-day free trial (no credit card required for the first 5 quotes). Download the mobile app, upload your first job photos, and our AI will generate a professional quote in under 60 seconds. Watch our "Getting Started" video tutorial for a complete walkthrough.'
            }
          },
          {
            '@type': 'Question',
            name: 'How does the AI photo-to-quote feature work?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Simply snap photos of the job site using your phone. Our AI analyzes the images, identifies materials needed, calculates quantities, and pulls real-time pricing from 50+ Australian suppliers (Bunnings, Reece, Mitre 10, etc.). It then generates a detailed, professional quote with line items, GST, and your branding—all in under 60 seconds.'
            }
          },
          {
            '@type': 'Question',
            name: 'What payment methods can my clients use?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Your clients can pay via credit/debit card, bank transfer, or direct debit. All payments are processed securely through Stripe with bank-level encryption. Funds are deposited directly into your nominated bank account within 1-2 business days.'
            }
          },
          {
            '@type': 'Question',
            name: 'Can I use MtaPlannerPro on my phone?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Absolutely! Our mobile app is available for iOS and Android. You can create quotes, send invoices, track jobs, manage schedules, and receive payment notifications—all from your phone on the job site. The app works offline and syncs when you\'re back online.'
            }
          },
          {
            '@type': 'Question',
            name: 'How accurate are the supplier prices?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Our supplier pricing is updated daily at 6am AEST from 50+ partners including Bunnings, Reece, Mitre 10, Total Tools, Tradelink, and Stratco. We maintain 99.8% price accuracy across 95,000+ products. Prices include your trade discount where applicable.'
            }
          },
          {
            '@type': 'Question',
            name: 'What happens after my 7-day free trial?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'After your 7-day trial (which includes 5 free quotes), you\'ll be automatically enrolled in the Starter plan at $49/month unless you cancel. You can upgrade to Pro ($99/month) or Enterprise (custom pricing) anytime. All plans include a 30-day money-back guarantee.'
            }
          },
          {
            '@type': 'Question',
            name: 'Can I customize my quotes and invoices?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes! Add your logo, business colors, terms & conditions, payment terms, and custom line items. You can also create quote templates for common jobs (e.g., "Bathroom Renovation", "Hot Water System Install") to save even more time.'
            }
          },
          {
            '@type': 'Question',
            name: 'How do I schedule jobs and manage my calendar?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Use the built-in calendar to schedule jobs, set reminders, and block out unavailable times. You can sync with Google Calendar or Apple Calendar. Clients receive automatic SMS/email reminders 24 hours before their appointment.'
            }
          },
          {
            '@type': 'Question',
            name: 'Does MtaPlannerPro integrate with Xero or MYOB?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes! We integrate with Xero, MYOB, QuickBooks, and other accounting software. Invoices and payments sync automatically, saving you hours of manual data entry. Set up takes less than 5 minutes.'
            }
          },
          {
            '@type': 'Question',
            name: 'What support is available if I need help?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'We offer 24/7 live chat support, email support (response within 2 hours), phone support (1300 123 456), and a comprehensive video tutorial library. Pro and Enterprise plans include priority support and a dedicated account manager.'
            }
          },
          {
            '@type': 'Question',
            name: 'Can I track my team\'s jobs and locations?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes! Pro and Enterprise plans include GPS tracking, team scheduling, and real-time job status updates. See where your team is, track travel time, and monitor job progress—all from your dashboard.'
            }
          },
          {
            '@type': 'Question',
            name: 'Is my data secure and backed up?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Absolutely. We use bank-level 256-bit encryption, store all data on Australian servers (AWS Sydney), and perform daily automated backups. We\'re ISO 27001 certified and fully GDPR compliant. Your data is never shared with third parties.'
            }
          }
        ]
      },
      {
        '@context': 'https://schema.org',
        '@type': 'VideoObject',
        name: 'Getting Started: Complete Setup Guide',
        description: 'Complete setup guide for MtaPlannerPro - learn how to set up your account, add business details, and create your first quote',
        thumbnailUrl: 'https://public.readdy.ai/ai/img_res/169c8fb5dca49a20a7ebf0aa9e3e5307.jpg',
        uploadDate: '2026-01-15',
        duration: 'PT8M42S',
        contentUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ'
      }
    ]
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedFAQ, setExpandedFAQ] = useState<string | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<VideoTutorial | null>(null);

  const categories = [
    { id: 'all', label: 'All Topics', icon: 'ri-apps-line' },
    { id: 'getting-started', label: 'Getting Started', icon: 'ri-rocket-line' },
    { id: 'quotes', label: 'Quotes & Invoices', icon: 'ri-file-text-line' },
    { id: 'payments', label: 'Payments', icon: 'ri-money-dollar-circle-line' },
    { id: 'scheduling', label: 'Scheduling', icon: 'ri-calendar-line' },
    { id: 'mobile', label: 'Mobile App', icon: 'ri-smartphone-line' },
    { id: 'integrations', label: 'Integrations', icon: 'ri-plug-line' },
    { id: 'billing', label: 'Billing & Plans', icon: 'ri-bank-card-line' },
  ];

  const faqs: FAQItem[] = [
    {
      id: 'faq-1',
      question: 'How do I get started with MtaPlannerPro?',
      answer: 'Getting started is easy! Sign up for a 7-day free trial (no credit card required for the first 5 quotes). Download the mobile app, upload your first job photos, and our AI will generate a professional quote in under 60 seconds. Watch our "Getting Started" video tutorial for a complete walkthrough.',
      category: 'getting-started',
    },
    {
      id: 'faq-2',
      question: 'How does the AI photo-to-quote feature work?',
      answer: 'Simply snap photos of the job site using your phone. Our AI analyzes the images, identifies materials needed, calculates quantities, and pulls real-time pricing from 50+ Australian suppliers (Bunnings, Reece, Mitre 10, etc.). It then generates a detailed, professional quote with line items, GST, and your branding—all in under 60 seconds.',
      category: 'quotes',
    },
    {
      id: 'faq-3',
      question: 'What payment methods can my clients use?',
      answer: 'Your clients can pay via credit/debit card, bank transfer, or direct debit. All payments are processed securely through Stripe with bank-level encryption. Funds are deposited directly into your nominated bank account within 1-2 business days.',
      category: 'payments',
    },
    {
      id: 'faq-4',
      question: 'Can I use MtaPlannerPro on my phone?',
      answer: 'Absolutely! Our mobile app is available for iOS and Android. You can create quotes, send invoices, track jobs, manage schedules, and receive payment notifications—all from your phone on the job site. The app works offline and syncs when you\'re back online.',
      category: 'mobile',
    },
    {
      id: 'faq-5',
      question: 'How accurate are the supplier prices?',
      answer: 'Our supplier pricing is updated daily at 6am AEST from 50+ partners including Bunnings, Reece, Mitre 10, Total Tools, Tradelink, and Stratco. We maintain 99.8% price accuracy across 95,000+ products. Prices include your trade discount where applicable.',
      category: 'quotes',
    },
    {
      id: 'faq-6',
      question: 'What happens after my 7-day free trial?',
      answer: 'After your 7-day trial (which includes 5 free quotes), you\'ll be automatically enrolled in the Starter plan at $49/month unless you cancel. You can upgrade to Pro ($99/month) or Enterprise (custom pricing) anytime. All plans include a 30-day money-back guarantee.',
      category: 'billing',
    },
    {
      id: 'faq-7',
      question: 'Can I customize my quotes and invoices?',
      answer: 'Yes! Add your logo, business colors, terms & conditions, payment terms, and custom line items. You can also create quote templates for common jobs (e.g., "Bathroom Renovation", "Hot Water System Install") to save even more time.',
      category: 'quotes',
    },
    {
      id: 'faq-8',
      question: 'How do I schedule jobs and manage my calendar?',
      answer: 'Use the built-in calendar to schedule jobs, set reminders, and block out unavailable times. You can sync with Google Calendar or Apple Calendar. Clients receive automatic SMS/email reminders 24 hours before their appointment.',
      category: 'scheduling',
    },
    {
      id: 'faq-9',
      question: 'Does MtaPlannerPro integrate with Xero or MYOB?',
      answer: 'Yes! We integrate with Xero, MYOB, QuickBooks, and other accounting software. Invoices and payments sync automatically, saving you hours of manual data entry. Set up takes less than 5 minutes.',
      category: 'integrations',
    },
    {
      id: 'faq-10',
      question: 'What support is available if I need help?',
      answer: 'We offer 24/7 live chat support, email support (response within 2 hours), phone support (1300 123 456), and a comprehensive video tutorial library. Pro and Enterprise plans include priority support and a dedicated account manager.',
      category: 'getting-started',
    },
    {
      id: 'faq-11',
      question: 'Can I track my team\'s jobs and locations?',
      answer: 'Yes! Pro and Enterprise plans include GPS tracking, team scheduling, and real-time job status updates. See where your team is, track travel time, and monitor job progress—all from your dashboard.',
      category: 'scheduling',
    },
    {
      id: 'faq-12',
      question: 'Is my data secure and backed up?',
      answer: 'Absolutely. We use bank-level 256-bit encryption, store all data on Australian servers (AWS Sydney), and perform daily automated backups. We\'re ISO 27001 certified and fully GDPR compliant. Your data is never shared with third parties.',
      category: 'getting-started',
    },
  ];

  const videoTutorials: VideoTutorial[] = [
    {
      id: 'video-1',
      title: 'Getting Started: Complete Setup Guide',
      duration: '8:42',
      thumbnail: 'https://readdy.ai/api/search-image?query=professional%20australian%20tradie%20in%20high%20visibility%20vest%20using%20tablet%20computer%20on%20construction%20site%20reviewing%20digital%20dashboard%20with%20modern%20interface%20bright%20outdoor%20lighting%20construction%20equipment%20in%20background%20tutorial%20style%20educational%20content%20clean%20composition&width=640&height=360&seq=help-video-001&orientation=landscape',
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      category: 'getting-started',
    },
    {
      id: 'video-2',
      title: 'Creating Your First Quote in 60 Seconds',
      duration: '3:15',
      thumbnail: 'https://readdy.ai/api/search-image?query=close%20up%20hands%20holding%20smartphone%20taking%20photos%20of%20construction%20materials%20timber%20planks%20and%20tools%20on%20workbench%20professional%20tradie%20creating%20digital%20quote%20on%20mobile%20app%20screen%20visible%20with%20pricing%20details%20bright%20workshop%20lighting%20tutorial%20demonstration&width=640&height=360&seq=help-video-002&orientation=landscape',
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      category: 'quotes',
    },
    {
      id: 'video-3',
      title: 'How to Send Invoices & Get Paid Faster',
      duration: '5:28',
      thumbnail: 'https://readdy.ai/api/search-image?query=professional%20electrician%20in%20work%20uniform%20sitting%20at%20desk%20reviewing%20invoice%20on%20laptop%20screen%20with%20payment%20notification%20on%20smartphone%20beside%20coffee%20cup%20modern%20office%20setting%20satisfied%20expression%20tutorial%20style&width=640&height=360&seq=help-video-003&orientation=landscape',
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      category: 'payments',
    },
    {
      id: 'video-4',
      title: 'Mobile App Tutorial: Work From Anywhere',
      duration: '6:50',
      thumbnail: 'https://readdy.ai/api/search-image?query=tradie%20plumber%20in%20blue%20work%20shirt%20using%20mobile%20phone%20app%20on%20job%20site%20with%20van%20in%20background%20managing%20schedule%20and%20jobs%20on%20smartphone%20screen%20outdoor%20construction%20setting%20professional%20mobile%20workflow%20tutorial&width=640&height=360&seq=help-video-004&orientation=landscape',
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      category: 'mobile',
    },
    {
      id: 'video-5',
      title: 'Scheduling Jobs & Managing Your Calendar',
      duration: '4:35',
      thumbnail: 'https://readdy.ai/api/search-image?query=construction%20business%20owner%20viewing%20large%20digital%20calendar%20on%20computer%20monitor%20with%20multiple%20job%20appointments%20color%20coded%20schedule%20planning%20workspace%20with%20construction%20plans%20and%20coffee%20modern%20office%20tutorial%20demonstration&width=640&height=360&seq=help-video-005&orientation=landscape',
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      category: 'scheduling',
    },
    {
      id: 'video-6',
      title: 'Integrating with Xero & MYOB',
      duration: '7:12',
      thumbnail: 'https://readdy.ai/api/search-image?query=professional%20accountant%20or%20tradie%20viewing%20accounting%20software%20integration%20on%20laptop%20screen%20showing%20xero%20and%20myob%20logos%20with%20data%20syncing%20animation%20modern%20office%20desk%20with%20calculator%20and%20documents%20tutorial%20style&width=640&height=360&seq=help-video-006&orientation=landscape',
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      category: 'integrations',
    },
  ];

  const filteredFAQs = faqs.filter((faq) => {
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    const matchesSearch = faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const filteredVideos = videoTutorials.filter((video) => {
    const matchesCategory = selectedCategory === 'all' || video.category === selectedCategory;
    const matchesSearch = video.title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/10 border border-teal-500/20 rounded-full mb-6">
            <i className="ri-customer-service-2-line text-teal-400"></i>
            <span className="text-teal-300 text-sm font-medium">24/7 Support Available</span>
          </div>

          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            How Can We Help You?
          </h1>
          <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
            Search our knowledge base, watch video tutorials, or contact our support team
          </p>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto relative">
            <i className="ri-search-line absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 text-xl"></i>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search for help articles, tutorials, or features..."
              className="w-full pl-14 pr-6 py-5 bg-white rounded-2xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 shadow-xl text-lg"
            />
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12 max-w-4xl mx-auto">
            {[
              { icon: 'ri-article-line', label: '120+ Articles', color: 'text-teal-400' },
              { icon: 'ri-video-line', label: '45+ Videos', color: 'text-amber-400' },
              { icon: 'ri-chat-3-line', label: 'Live Chat', color: 'text-rose-400' },
              { icon: 'ri-phone-line', label: '1300 123 456', color: 'text-sky-400' },
            ].map((stat, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <i className={`${stat.icon} ${stat.color} text-3xl mb-2`}></i>
                <p className="text-white font-semibold text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-slate-50 border-b border-slate-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-sm whitespace-nowrap transition-all cursor-pointer ${
                  selectedCategory === cat.id
                    ? 'bg-teal-500 text-white shadow-lg shadow-teal-500/20'
                    : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                <i className={`${cat.icon} text-base`}></i>
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Getting Started Guide */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Quick Start Guide
            </h2>
            <p className="text-slate-600 text-lg">
              Get up and running in 3 simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '1',
                icon: 'ri-user-add-line',
                title: 'Create Your Account',
                description: 'Sign up in 60 seconds. Add your business details, logo, and payment info.',
                cta: 'Start Free Trial',
                link: '/demo',
              },
              {
                step: '2',
                icon: 'ri-camera-line',
                title: 'Upload Your First Job',
                description: 'Snap photos of the job site. Our AI generates a professional quote instantly.',
                cta: 'Watch Tutorial',
                link: '#videos',
              },
              {
                step: '3',
                icon: 'ri-send-plane-line',
                title: 'Send & Get Paid',
                description: 'Send the quote to your client. Track when they view it and get paid online.',
                cta: 'See How It Works',
                link: '#faqs',
              },
            ].map((step, i) => (
              <div key={i} className="relative bg-gradient-to-br from-slate-50 to-white rounded-2xl p-8 border border-slate-200 hover:shadow-xl transition-all group">
                <div className="absolute -top-4 -left-4 w-12 h-12 bg-teal-500 text-white rounded-xl flex items-center justify-center text-xl font-bold shadow-lg">
                  {step.step}
                </div>
                <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className={`${step.icon} text-teal-600 text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
                <p className="text-slate-600 mb-6">{step.description}</p>
                <a
                  href={step.link}
                  className="inline-flex items-center gap-2 text-teal-600 font-semibold hover:gap-3 transition-all cursor-pointer"
                >
                  {step.cta}
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Tutorials */}
      <section id="videos" className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-2">
                Video Tutorials
              </h2>
              <p className="text-slate-600">
                Watch step-by-step guides to master MtaPlannerPro
              </p>
            </div>
            <div className="flex items-center gap-2 text-slate-600">
              <i className="ri-video-line text-teal-500"></i>
              <span className="font-semibold">{filteredVideos.length} videos</span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredVideos.map((video) => (
              <div
                key={video.id}
                onClick={() => setSelectedVideo(video)}
                className="bg-white rounded-xl overflow-hidden border border-slate-200 hover:shadow-xl transition-all cursor-pointer group"
              >
                <div className="relative">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center">
                      <i className="ri-play-fill text-teal-600 text-2xl ml-1"></i>
                    </div>
                  </div>
                  <div className="absolute bottom-3 right-3 px-3 py-1 bg-black/80 rounded-lg text-white text-xs font-semibold">
                    {video.duration}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-teal-600 transition-colors">
                    {video.title}
                  </h3>
                  <div className="flex items-center gap-2 text-slate-500 text-sm">
                    <i className="ri-play-circle-line"></i>
                    <span>Watch tutorial</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faqs" className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-slate-600 text-lg">
              Find answers to common questions about MtaPlannerPro
            </p>
          </div>

          <div className="space-y-4">
            {filteredFAQs.map((faq) => (
              <div
                key={faq.id}
                className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden hover:border-teal-300 transition-all"
              >
                <button
                  onClick={() => setExpandedFAQ(expandedFAQ === faq.id ? null : faq.id)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left cursor-pointer group"
                >
                  <span className="text-lg font-semibold text-slate-900 pr-4 group-hover:text-teal-600 transition-colors">
                    {faq.question}
                  </span>
                  <i
                    className={`ri-arrow-down-s-line text-2xl text-slate-400 transition-transform ${
                      expandedFAQ === faq.id ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    expandedFAQ === faq.id ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <div className="px-6 pb-5 text-slate-600 leading-relaxed">
                    {faq.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredFAQs.length === 0 && (
            <div className="text-center py-12">
              <i className="ri-search-line text-slate-300 text-5xl mb-4"></i>
              <p className="text-slate-500 text-lg">
                No results found. Try a different search term or category.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Contact Support */}
      <section className="py-16 bg-gradient-to-br from-teal-600 to-cyan-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Still Need Help?
          </h2>
          <p className="text-teal-100 text-lg mb-10">
            Our support team is available 24/7 to assist you
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: 'ri-chat-3-line',
                title: 'Live Chat',
                description: 'Chat with our team now',
                cta: 'Start Chat',
                color: 'bg-white text-teal-600',
              },
              {
                icon: 'ri-mail-line',
                title: 'Email Support',
                description: 'support@mtaplannerpro.com',
                cta: 'Send Email',
                color: 'bg-teal-500 text-white border-2 border-white',
              },
              {
                icon: 'ri-phone-line',
                title: 'Phone Support',
                description: '1300 123 456',
                cta: 'Call Now',
                color: 'bg-white text-teal-600',
              },
            ].map((contact, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className={`w-14 h-14 ${contact.color} rounded-xl flex items-center justify-center mx-auto mb-4`}>
                  <i className={`${contact.icon} text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{contact.title}</h3>
                <p className="text-teal-100 text-sm mb-4">{contact.description}</p>
                <button className="px-6 py-2.5 bg-white text-teal-600 rounded-lg font-semibold hover:bg-teal-50 transition-colors cursor-pointer whitespace-nowrap">
                  {contact.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {selectedVideo && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-6"
          onClick={() => setSelectedVideo(null)}
        >
          <div
            className="relative w-full max-w-4xl bg-slate-900 rounded-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedVideo(null)}
              className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white z-10 cursor-pointer transition-colors"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
            <div className="aspect-video">
              <iframe
                src={selectedVideo.videoUrl}
                title={selectedVideo.title}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="p-6">
              <h3 className="text-xl font-bold text-white mb-2">{selectedVideo.title}</h3>
              <div className="flex items-center gap-4 text-slate-400 text-sm">
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {selectedVideo.duration}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
